import {Component, OnInit, Inject} from '@angular/core';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {BehaviorSubject, from} from 'rxjs';
import {
  debounceTime,
  groupBy,
  map,
  mergeMap,
  skip,
  startWith,
  switchMap,
  take,
  takeUntil, tap,
  toArray
} from 'rxjs/operators';
import {Subject} from "rxjs/internal/Subject";
import {CoreService} from "../../core/services/core.service";
import {SelectionModel} from "@angular/cdk/collections";
import {fromArray} from "rxjs/internal/observable/fromArray";


export interface GroupSelection {
  all: boolean;
  some: boolean;
  display: boolean;
}

export interface RatingSelection {
  id: number;
  name: string;
  value: string;
  // name: string;
  // name_ru: string;
  group: string;
  // max_ranks: number;
  // min_ranks: number;
  // num_ranks: number;
}

@Component({
  selector: 'app-rating-select-dialog',
  templateUrl: './rating-select-dialog.component.html',
  styleUrls: ['./rating-select-dialog.component.scss']
})
export class RatingSelectDialogComponent implements OnInit {

  form: FormGroup;

  universities = [];

  groups: Array<Array<RatingSelection>> = [];
  filteredGroups$ = new BehaviorSubject([]);
  filter: FormControl = new FormControl('');
  selection: SelectionModel<RatingSelection>;
  groupSelected: Array<GroupSelection> = [];
  allSelected: GroupSelection = {all: false, some: false, display: true};
  varLabel: string = '';
  isValid: boolean = false;
  loading: boolean = true;
  destroyed$ = new Subject();

  ratings: [];
  filters: [];
  keys: [];

  constructor(private formBuilder: FormBuilder,
              private coreService: CoreService,
              private dialogRef: MatDialogRef<RatingSelectDialogComponent>,
              @Inject(MAT_DIALOG_DATA) private data: any) {

    this.selection = new SelectionModel<RatingSelection>(true, []);
    this.ratings = data && data.ratings ? data.ratings : [];
    this.filters = data && data.filters ? data.filters : [];
    this.keys = this.ratings.map(v => v['indicator']) as [];

    this.prepareOptions();

  }

  ngOnInit() {

    this.filter.valueChanges.pipe(
      takeUntil(this.destroyed$),
      startWith(''),
      debounceTime(350),
      map(value => this._filterValue(value))
    ).subscribe(value => {
      this.filteredGroups$.next(value);
    });

    this.selection.changed.subscribe(v => {
      if (this.selection.selected.length == 1) {
        this.varLabel = this.selection.selected[0].name;
      } else if (this.selection.selected.length) {
        this.varLabel = `${this.selection.selected[0].name} и ещё ${this.selection.selected.length - 1}`;
      } else {
        this.varLabel = '';
      }

      this.isValid = this.selection.selected.length > 0;

    });
    this.filteredGroups$.subscribe((v: RatingSelection[][]) => {
      if (this.loading == false) {
        for (let i = 0; i < v.length; i++) {
          this.updateSomeSelected(v[i], i);
        }
      }
    });

  }


  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }


  close() {
    this.dialogRef.close();
  }

  complete() {
    if (!this.isValid) {
      return;
    }

    let payload = {
      label: this.varLabel,
      val: this.selection.selected,
    };

    this.dialogRef.close({data: payload});
  }

  prepareOptions() {
    this.loading = true;
    const selected_values = (this.data && this.data.val) ? this.data.val : [];
    const ratings = (this.data && this.data.ratings) ? this.data.ratings : [];
    // const selected_values = selected.map(x => x.value);

    fromArray(ratings)
      .pipe(
        takeUntil(this.destroyed$),
        map(v => {
          return {
            id: v['id'],
            name: v['public_name'],
            value: v['value'],
            group: v['group'],

          } as RatingSelection
        }),
        groupBy((v: RatingSelection) => v.group),
        mergeMap(group => group.pipe(toArray())),
    ).subscribe(v => {
        this.groups.push(v);
        this.groupSelected.push({all: false, some: false, display: true} as GroupSelection);
        for(let i=0 ; i<v.length ; i++) {
          if (selected_values.includes(v[i].value)) {
            this.selection.select(v[i]);
          }
        }

        this.filteredGroups$.next(this.groups);
        this.loading = false;
        this.isValid = this.selection.selected.length > 0;
    });
  }

  private _filterValue(value: any): Array<Array<RatingSelection>> {
    const filterValue = typeof value === 'string' ? value.toLowerCase() : '';
    let groups: Array<Array<RatingSelection>> = [];
    if (filterValue == '') {
      groups = this.groups.slice(0);
    } else {
      for (let i = 0; i < this.groups.length; i++) {
        let universities = this.groups[i].filter((v: RatingSelection) => v.name.toLowerCase().indexOf(filterValue) !== -1 || v.group.toString().toLowerCase().indexOf(filterValue) !== -1)
        groups.push(universities);
      }
    }
    return groups;
  }


  isSomeSelected(group, index): GroupSelection {
    let all = true;
    let some = false;
    for (let i = 0; i < group.length; i++) {
      const selected = this.selection.isSelected(group[i]);
      all = all && selected;
      some = some || selected;
    }
    return {all: all, some: !all && some, display: true};
  }

  updateSomeSelected(group, index) {
    const res = this.isSomeSelected(group, index);
    this.groupSelected[index].all = res.all;
    this.groupSelected[index].some = res.some;
  }

  updateAllSelected() {
    this.allSelected.all = true;
    this.allSelected.some = false;
    let all = false;
    let some = false;
    for(let i=0 ; i<this.groupSelected.length ; i++) {
      if(!this.groupSelected[i].all) {
        this.allSelected.all = false;
      }
      all = all || this.groupSelected[i].all;
      some = some || this.groupSelected[i].some;
    }
    if(this.allSelected.all) {
      this.allSelected.some = false
    } else {
      this.allSelected.some = some || all;
    }
  }

  toggle(groupIndex, row) {
    this.selection.toggle(row);

    this.filteredGroups$.pipe(
      switchMap((v: RatingSelection[][]) => from(v)
        .pipe(
          skip(groupIndex),
          take(1)
        )
      )
    ).subscribe((v: RatingSelection[]) => {
      this.updateSomeSelected(v, groupIndex);
      this.updateAllSelected();
    }).unsubscribe();
  }

  masterToggle(index) {
    this.filteredGroups$.pipe(
      switchMap((v: RatingSelection[][]) => from(v)
        .pipe(
          skip(index),
          take(1)
        )
      )
    ).subscribe((v: RatingSelection[]) => {

      this.updateSomeSelected(v, index);

      for (let i = 0; i < v.length; i++) {
        if (this.groupSelected[index].all) {
          this.selection.deselect(v[i]);
        } else {
          this.selection.select(v[i]);
        }
      }

      this.updateSomeSelected(v, index);
      this.updateAllSelected();
    }).unsubscribe();

  }

  allToggle() {
    const all = this.allSelected.all;
    let index = 0;
    this.filteredGroups$.pipe(
      switchMap((v: RatingSelection[][]) => from(v))
    ).subscribe((group: RatingSelection[]) => {

      // this.updateSomeSelected(v, index);

      for (let i = 0; i < group.length; i++) {
        if (all) {
          this.selection.deselect(group[i]);
        } else {
          this.selection.select(group[i]);
        }
      }
      this.updateSomeSelected(group, index);
      index++;
    }).unsubscribe();
    this.allSelected.all = !all;
  }

  groupTrackByFn(index, item) {
    return `c-${index}`; // уникальный идентификатор, соответствующий элементу
  }

  ratingTrackByFn(index, item) {
    return `u-${item.id}`; // уникальный идентификатор, соответствующий элементу
  }
}
